import { useEffect, useState } from "react";
import { X, Loader2, FileText, Download } from "lucide-react";
import { api, apiClient } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { formatCurrency } from "@/lib/format";

export const StatementModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const [clients, setClients] = useState(null);
  const [dl, setDl] = useState(null);

  useEffect(() => {
    if (!open) return;
    setClients(null);
    api.dmsClients().then(setClients).catch(() => { setClients([]); toast("Could not load clients", "error"); });
  }, [open, toast]);

  if (!open) return null;

  const download = async (c) => {
    setDl(c.client_name);
    try {
      const res = await apiClient.get("/dms/client-statement", {
        params: { client: c.client_name }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${c.client_name.replace(/[^\w]+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Statement download failed", "error");
    } finally {
      setDl(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="statement-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4 max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between shrink-0">
          <div>
            <h3 className="font-bold text-base flex items-center gap-2"><FileText size={16} className="text-[#0e6939]" /> Client statements</h3>
            <p className="text-xs muted mt-0.5">Branded PDF of all quotations, payments and balances per client</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="statement-close">
            <X size={16} />
          </button>
        </div>
        {clients === null ? (
          <div className="flex items-center gap-2 text-sm muted py-4"><Loader2 size={14} className="animate-spin" /> Loading clients…</div>
        ) : clients.length === 0 ? (
          <p className="text-sm muted py-4">No quotations with client names yet.</p>
        ) : (
          <div className="divide-y divide-slate-100 overflow-y-auto min-h-0">
            {clients.map((c) => (
              <div key={c.client_name} className="py-2.5 flex items-center justify-between gap-3" data-testid={`statement-client-${c.client_name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{c.client_name}</div>
                  <div className="text-xs muted">
                    {c.count} quotation{c.count === 1 ? "" : "s"}
                    {c.outstanding > 0 && (
                      <> · <span className="text-red-600 font-semibold">{formatCurrency(c.outstanding, c.currency)} owed</span></>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`statement-dl-${c.client_name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => download(c)}
                  disabled={dl === c.client_name}
                  className="btn btn-ghost text-xs shrink-0 border border-slate-200"
                >
                  {dl === c.client_name ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Statement
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
